import React, { useEffect, useMemo } from 'react';

import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Wizard } from 'src/app/components/lib/wizard';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';
import { useDiscounts, useUpdateDiscountDetailV2, useUpdateDiscountMenuCard } from 'src/app/queries/discounts/discount';
import { routes } from 'src/app/routes';
import { useNotificationActions } from 'src/app/state/notifications';

import { calculateMaxMenuRank } from '../helpers';

import { getRequestBody } from './get-request-body';
import { EligibleProductsModal } from './shared/eligible-products-modal';
import { FilterModal } from './shared/filter-modal';
import { isRequirementConditionValid } from './shared/helpers';
import { DiscountWizardStateContext, useDiscountWizardState, useDiscountWizardStateContext } from './state';
import { OnlineDetailsStepActions } from './state/online-details';
import { Configure, Details, OnlineDetails, Requirements, Rewards, Summary } from './steps';
import { SummarySidebar } from './summary-sidebar';

import type { InitialWizardState } from '../types';
import type { WizardProps, WizardStep } from 'src/app/components/lib/wizard';

const steps: WizardStep[] = [
  { title: 'Configure', Component: Configure, hideBackButton: true },
  { title: 'Details', Component: Details },
  { title: 'Requirements', Component: Requirements },
  { title: 'Rewards', Component: Rewards },
  { title: 'Online details', Component: OnlineDetails },
  { title: 'Summary', Component: Summary },
];

type DiscountWizardProps = {
  discountId?: number;
  initialState?: InitialWizardState;
  isWizardActive: boolean;
  onClose?: () => void;
  showNotificationLink?: boolean;
};

export function DiscountWizard({
  discountId,
  initialState,
  isWizardActive,
  showNotificationLink = false,
  onClose: onCloseCallback,
}: DiscountWizardProps) {
  const discountWizardState = useDiscountWizardState(discountId, initialState);

  return (
    <DiscountWizardStateContext.Provider value={discountWizardState}>
      <DiscountWizardContent
        isWizardActive={isWizardActive}
        showNotificationLink={showNotificationLink}
        onClose={onCloseCallback}
      />
    </DiscountWizardStateContext.Provider>
  );
}

type DiscountWizardContentProps = {
  isWizardActive: boolean;
  onClose?: () => void;
  showNotificationLink?: boolean;
};

function DiscountWizardContent({
  isWizardActive,
  showNotificationLink = false,
  onClose: onCloseCallback,
}: DiscountWizardContentProps) {
  const { data: discounts = [] } = useDiscounts({ enabled: isWizardActive });

  const ecommDiscountMaxRank = useMemo(() => {
    const ecommDiscounts = discounts.filter((discount) => discount.IsAvailableOnline);
    return calculateMaxMenuRank(ecommDiscounts);
  }, [discounts]);

  const {
    configureStepState,
    defaultStepToOpenOn,
    detailsStepState,
    dirty,
    discountId,
    loading,
    onlineDetailsStepDispatch,
    onlineDetailsStepState,
    requirementsStepState,
    reset,
    rewardsStepState,
    showEligibleProductsModal,
    eligibleProductsModalRequirement,
  } = useDiscountWizardStateContext();
  const { mutate: createDiscount, isLoading } = useUpdateDiscountDetailV2();
  const { mutate: updateMenuCard } = useUpdateDiscountMenuCard();
  const notifications = useNotificationActions();
  const isCreatingDiscount = discountId === 0;
  const useDiscountsLoyaltyMultiplierFix = useLDFlag(LD.DISCOUNTS_LOYALTY_MULTIPLIER_FIX, false);

  const onClose = () => {
    reset();
    onCloseCallback?.();
  };

  useEffect(() => {
    onlineDetailsStepDispatch(
      {
        type: OnlineDetailsStepActions.SET_RANK,
        payload: ecommDiscountMaxRank + 1,
      },
      true
    );
  }, [ecommDiscountMaxRank, onlineDetailsStepDispatch]);

  const onCreateClick: WizardProps['onCompleteButtonClick'] = async (reset) => {
    createDiscount(
      getRequestBody({
        configureStepState,
        detailsStepState,
        onlineDetailsStepState,
        requirementsStepState,
        rewardsStepState,
        discountId,
        useDiscountsLoyaltyMultiplierFix,
      }),
      {
        onSuccess: (id) => {
          // After the discount saves, we use the id that is returned from the created discount to save the menu card details (Online Details step) by hitting a separate endpoint
          updateMenuCard({
            DiscountMenuDisplay: {
              DiscountId: id,
              DiscountMenuDisplayId: onlineDetailsStepState.displayId,
              MenuDisplayDescription: onlineDetailsStepState.description,
              MenuDisplayImageUrl: onlineDetailsStepState.imageUrl,
              MenuDisplayName: onlineDetailsStepState.onlineName,
            },
          });

          reset();
          onClose();

          // Show link back to discount in the post-creation notification
          if (showNotificationLink && isCreatingDiscount) {
            notifications.success({
              message: 'Discount created successfully.',
              subContent: (
                <StyledLink to={`${routes.marketing.discounts.index}/all/${id}`}>View and edit discount</StyledLink>
              ),
              sticky: true,
            });
          } else {
            notifications.success({ message: `Discount ${isCreatingDiscount ? 'created' : 'updated'} successfully.` });
          }
        },
        onError: (e) => {
          console.error(e);
          notifications.apiError({
            message: `Error ${isCreatingDiscount ? 'creating' : 'updating'} discount. Please try again.`,
          });
        },
      }
    );
  };

  const verb = useMemo(() => (isCreatingDiscount ? 'Create' : 'Edit'), [isCreatingDiscount]);
  const completeButtonLabel = useMemo(
    () => (isCreatingDiscount ? 'Create discount' : 'Update discount'),
    [isCreatingDiscount]
  );

  return (
    <>
      <Wizard
        completeButtonDisabled={!isRequirementConditionValid(requirementsStepState)}
        completeButtonLabel={completeButtonLabel}
        defaultStepToOpenOn={defaultStepToOpenOn ?? (isCreatingDiscount ? 0 : steps.length - 1)}
        drawerAriaLabel={`${verb} discount wizard`}
        enableNavigationByClicks
        isCompleteRequestInFlight={isLoading}
        loading={loading}
        open={isWizardActive}
        showWarningOnExit={dirty}
        Sidebar={SummarySidebar}
        steps={steps}
        title={`${verb} discount`}
        warningText={<ExitModalWarningText />}
        onClose={onClose}
        onCompleteButtonClick={onCreateClick}
      />
      {requirementsStepState.activeFilter.filter && requirementsStepState.activeFilter.requirement && (
        <FilterModal
          filterResourceType={requirementsStepState.activeFilter.filter}
          requirement={requirementsStepState.activeFilter.requirement}
        />
      )}
      {rewardsStepState.activeFilter && (
        <FilterModal filterResourceType={rewardsStepState.activeFilter} requirement='reward' />
      )}
      {showEligibleProductsModal && <EligibleProductsModal requirement={eligibleProductsModalRequirement} />}
    </>
  );
}

function ExitModalWarningText() {
  return (
    <>
      Exiting discount creation will discard all entered details.
      <br />
      Are you sure you want to exit?
    </>
  );
}

const StyledLink = styled(Link)`
  color: var(--color-brand-secondary-sapphire);
  text-decoration: underline;
  font: var(--font-regular-14pt-semibold);

  &:hover {
    color: var(--color-brand-secondary-sapphire-dark);
  }

  &:visited {
    color: var(--color-brand-secondary-sapphire);
  }
`;
